import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  fieldNameMetadataResolve,
  GRID_DATETIME_FIELD_KEYS,
  Log,
  normalizeGridValue,
  profileDtoAtom,
  RydeUiUnit,
  translateGridValue,
} from "@ryde/shared";

import { ICsvColumnTransformer } from "../models";
import { useCsvTextCellTransformer } from "../use-csv-text-cell.transformer";
import { useCsvTransformerControlsMap } from "./map-csv-transformer-controls";

export const useCsvCellTransformer = ({ entity }: { entity: RydeUiUnit }) => {
  const { csvTransformerControlsMap } = useCsvTransformerControlsMap();
  const { getTextValue } = useCsvTextCellTransformer();
  const profile = useRecoilValue(profileDtoAtom);
  const { t } = useTranslation();

  const csvCellTransformer = useEvent(
    async (props: ICsvColumnTransformer): Promise<string | undefined> => {
      const {
        id,
        formAttributes,
        gridAttributes,
        name: fieldNameRaw,
        value: valueRaw,
        controlInfoName,
        isColumn,
      } = props;

      const fieldNameResolved = fieldNameMetadataResolve(
        fieldNameRaw,
        isColumn,
      );

      const propsNormalized: ICsvColumnTransformer = {
        ...props,
        ...(controlInfoName &&
        // to avoid unnecessary props providing
        GRID_DATETIME_FIELD_KEYS.includes(controlInfoName)
          ? {
              dateFormat: profile?.metadata?.dateFormat,
              timeFormat: profile?.metadata?.timeFormat,
            }
          : {}),
        name: fieldNameResolved,
        value: normalizeGridValue({
          valueToProcess: valueRaw,
          fieldNameResolved: fieldNameRaw,
          entity,
        }),
        valueTranslated: translateGridValue({
          valueToProcess: valueRaw,
          formAttributes,
          gridAttributes,
          skipBooleanSelectListLabelConversion: true,
          t,
        }),
      };

      const mapId = `${fieldNameRaw}_${id}`;

      if (!controlInfoName) {
        return getTextValue({ ...propsNormalized });
      }

      const csvCellTransformerFnName =
        csvTransformerControlsMap[controlInfoName];

      if (!csvCellTransformerFnName) {
        Log.warn("csv cell transformer unknown cell transformer");
        return getTextValue({ ...propsNormalized });
      }

      Log.log(`csv cell transformer ${controlInfoName}, mapId: ${mapId}`);
      return csvCellTransformerFnName({ ...propsNormalized });
    },
  );

  return {
    csvCellTransformer,
  };
};
